import { Project, Experience, Tool, Thought } from './types'

export const PROJECTS: Project[] = [
  {
    id: '1',
    slug: 'p01-affirmation-generator',
    title: 'p01_affirmation generator',
    description:
      'An AI-powered generator that crafts personalized, context-aware affirmations to boost mental clarity.',
    problem:
      'Daily stress and negative self-talk often hinder productivity and mental well-being.',
    solution:
      'Built a lightweight engine using Gemini to generate tailored affirmations based on user mood and current goals.',
    year: '2025',
    category: 'AI Implementation',
    coverImage:
      'https://images.unsplash.com/photo-1499750310107-5fef28a66643?q=80&w=1600&auto=format&fit=crop',
    accentColor: '#1A1A1A',
    status: 'published',
  },
  {
    id: '2',
    slug: 'p02-ai-habit-tracker',
    title: 'p02_ai habit tracker',
    description:
      'A predictive habit tracking system that analyzes patterns and suggests optimizations for daily routines.',
    problem:
      "Traditional trackers are passive; they don't tell you *why* you are failing a habit.",
    solution:
      'Integrated a simple ML model to correlate external factors (weather, day of week) with habit success rates.',
    year: '2025',
    category: 'AI Productivity',
    coverImage:
      'https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?q=80&w=1600&auto=format&fit=crop',
    accentColor: '#111111',
    status: 'published',
  },
  {
    id: '3',
    slug: 'p03-personal-vibe-dashboard',
    title: 'p03_personal vibe dashboard',
    description:
      'A central command center for personal data, wellness metrics, and ambient "vibe" control.',
    problem:
      'Digital fragmentation: wellness data is scattered across five different apps.',
    solution:
      'Unified dashboard with real-time weather, focus-timers, and a "mood-ring" background that shifts with daily progress.',
    year: '2025',
    category: 'Lifestyle / Dashboard',
    coverImage:
      'https://images.unsplash.com/photo-1555774698-0b77e0d5fac6?q=80&w=1600&auto=format&fit=crop',
    accentColor: '#0A0A0A',
    status: 'published',
  },
  {
    id: '5',
    slug: 'p05-voice-to-task-assistant',
    title: 'p05_voice to task assistant',
    description:
      'Hands-free task management that turns spoken ideas into structured project cards.',
    problem:
      'Inspiration strikes when hands are busy (driving, cooking, walking), leading to lost ideas.',
    solution:
      'Utilized Gemini Flash Audio for low-latency transcription and automated task categorization.',
    year: '2025',
    category: 'AI Productivity',
    coverImage:
      'https://images.unsplash.com/photo-1478737270239-2fccd27ee1f3?q=80&w=1600&auto=format&fit=crop',
    accentColor: '#000000',
    status: 'published',
  },
]

export const THOUGHTS: Thought[] = [
  {
    id: '1',
    slug: 'speed-as-a-feature',
    title: '[LOCAL] speed is the only true moate',
    excerpt: 'Why shipping fast beats shipping perfect every single time.',
    content:
      'In the age of AI, the distance between idea and execution has collapsed. The only advantage left is the speed of your feedback loop.',
    category: 'strategy',
    published_at: '2025-01-15',
  },
  {
    id: '2',
    slug: 'minimalist-ai-ux',
    title: '[LOCAL] ai ux: less is more human',
    excerpt: 'Conversational interfaces are just the beginning.',
    content:
      "Good AI UX shouldn't feel like talking to a machine. It should feel like the environment is anticipating your next move.",
    category: 'design',
    published_at: '2025-01-20',
  },
  {
    id: '3',
    slug: 'future-of-coding',
    title: '[LOCAL] coding is now a language art',
    excerpt: 'The shift from syntax to semantics.',
    content:
      'We are no longer writing instructions for machines; we are describing outcomes to collaborators.',
    category: 'engineering',
    published_at: '2025-01-22',
  },
  {
    id: '4',
    slug: 'dopamine-loops',
    title: '[LOCAL] designing for focus loops',
    excerpt: 'How to build tools that help people stay in flow.',
    content:
      'Digital tools should be dopamine-neutral until the work is done. Then, celebrate.',
    category: 'psychology',
    published_at: '2025-01-25',
  },
]

export const EXPERIENCES: Experience[] = [
  {
    period: '2024 — Present',
    company: 'Independent · 100 Projects Challenge',
    role: 'AI Product Designer & Builder',
    description:
      'Building 100 end-to-end, fully functional products using AI. Exploring Generative AI, automation, agentic workflows, and UX patterns by shipping real products.',
  },
  {
    period: '2021 — Present',
    company: 'Piramal Finance',
    role: 'Lead UX Designer — Customer Experience',
    description:
      'Led CX initiatives across lending and financial platforms. Designed end-to-end loan journeys, launched high-impact products, and built a scalable design system.',
  },
  {
    period: '2020 — 2021',
    company: 'Decathlon',
    role: 'UX UI Designer — Omni Commerce / Phygital',
    description:
      'Improved usability and conversion across web and app. Contributed to product redesigns and design system foundations.',
  },
  {
    period: '2018 — 2020',
    company: 'Paytm',
    role: 'UX UI Designer — Mutual Fund App',
    description:
      'Designed onboarding and investment flows for Paytm Money. Worked on illustrations, animations, and marketing visuals.',
  },
  {
    period: '2016 — 2018',
    company: 'Deck',
    role: 'Visual Designer',
    description:
      'Delivered branding, illustrations, icons, graphics, and animated visuals for digital products.',
  },
]

export const TOOLS: Tool[] = [
  {
    name: 'Figma',
    category: 'Product Design',
    icon: '🎨',
    logoUrl:
      'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg',
  },
  {
    name: 'Next.js',
    category: 'Frontend',
    icon: '⚛️',
    logoUrl:
      'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg',
  },
  {
    name: 'Tailwind',
    category: 'Styling',
    icon: '🌊',
    logoUrl:
      'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg',
  },
  {
    name: 'Gemini API',
    category: 'AI Integration',
    icon: '✨',
    logoUrl:
      'https://www.gstatic.com/lamda/images/gemini_sparkle_v002_d4735304ff6292a690345.svg',
  },
  {
    name: 'Vercel',
    category: 'Hosting',
    icon: '▲',
    logoUrl:
      'https://assets.vercel.com/image/upload/v1588805858/repositories/vercel/logo.png',
  },
  {
    name: 'Framer',
    category: 'Animation',
    icon: '💠',
    logoUrl:
      'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/framermotion/framermotion-original.svg',
  },
  {
    name: 'React',
    category: 'Frontend',
    icon: '⚛️',
    logoUrl:
      'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
  },
  {
    name: 'TypeScript',
    category: 'Language',
    icon: '🔷',
    logoUrl:
      'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg',
  },
  {
    name: 'Supabase',
    category: 'Backend',
    icon: '🗄️',
    logoUrl:
      'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/supabase/supabase-original.svg',
  },
  {
    name: 'Magic Patterns',
    category: 'AI Design',
    icon: '✦',
    logoUrl: 'https://magicpatterns.com/favicon.ico',
  },
  {
    name: 'VS Code',
    category: 'Editor',
    icon: '💻',
    logoUrl:
      'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg',
  },
  {
    name: 'Cloudinary',
    category: 'Media',
    icon: '☁️',
    logoUrl:
      'https://res.cloudinary.com/cloudinary-marketing/image/upload/v1699915400/creative_source/Logo/PNG/cloudinary_logo_blue_0720_2x.png',
  },
  {
    name: 'Firebase',
    category: 'Backend',
    icon: '🔥',
    logoUrl:
      'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg',
  },
  {
    name: 'Appify',
    category: 'No-Code',
    icon: '📱',
    logoUrl:
      'https://cdn.prod.website-files.com/5e6c01bb5212506d6c119069/5e6c01bb52125009e5119099_apify-logo-white.svg',
  },
  {
    name: 'Google AI Studio',
    category: 'AI Platform',
    icon: '🤖',
    logoUrl: 'https://www.gstatic.com/aistudio/ai_studio_favicon_32x32.png',
  },
  {
    name: 'Claude',
    category: 'AI Assistant',
    icon: '🧠',
    logoUrl: 'https://claude.ai/favicon.ico',
  },
  {
    name: 'ChatGPT',
    category: 'AI Assistant',
    icon: '💬',
    logoUrl: 'https://cdn.oaistatic.com/assets/favicon-o20kmmos.svg',
  },
  {
    name: 'GitHub Copilot',
    category: 'AI Coding',
    icon: '🤖',
    logoUrl: 'https://github.githubassets.com/favicons/favicon.svg',
  },
]
